import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    useBookingWizard,
    getSelectedBundle,
    getPerPersonCount,
    isOptionStepComplete,
    isScheduleStepComplete,
    isParticipantsStepComplete,
    FREE_TEXT_MAX_LENGTH
} from './BookingWizardContext';

// Step 3 — Dati dei partecipanti.
// Partecipante 1 = chi prenota: nome/cognome/nascita, precompilati dai dati di acquisto del
// profilo (modificabili). Partecipanti successivi: solo data di nascita.
// - perPerson: tanti partecipanti quanti scelti con lo stepper (numero fisso)
// - perHour/flat: partecipante 1 + "aggiungi partecipante" facoltativo (tetto = maxQty per flat)
// Seguono le domande personalizzate dell'attività (bookingCustomFields) e le Osservazioni.
const StepParticipants = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { activityData, booking, updateBooking } = useBookingWizard();
    const userId = useSelector(state => state.user.id);
    const token = localStorage.getItem('ticino-wild-token');

    const bundle = getSelectedBundle(activityData, booking);
    const bundleType = bundle ? (bundle.type || 'flat') : null;
    // perPerson: il numero di partecipanti è la somma delle quantità delle opzioni selezionate
    // (una sola, o più opzioni combinate — es. adulti + bambini)
    const perPersonCount = getPerPersonCount(activityData, booking);
    const isPerPerson = perPersonCount != null;
    const optionComplete = isOptionStepComplete(activityData, booking);
    const scheduleComplete = isScheduleStepComplete(activityData, booking);

    // perPerson: allinea il numero di schede partecipante alle quantità scelte allo step 1
    useEffect(() => {
        if (!isPerPerson || !perPersonCount) return;
        if (booking.participants.length === perPersonCount) return;
        const participants = [...booking.participants];
        while (participants.length < perPersonCount) participants.push({ name: '', surname: '', birthdate: '' });
        participants.length = perPersonCount;
        updateBooking({ participants });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPerPerson, perPersonCount, booking.participants.length]);

    // Precompila il partecipante 1 coi dati di acquisto del profilo (solo se i campi sono ancora vuoti)
    useEffect(() => {
        const first = booking.participants[0];
        if (!userId || !first || first.name || first.surname || first.birthdate) return;
        const prefill = async () => {
            try {
                const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/get-purchase-data/${userId}`;
                const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
                const response = await fetch(url, { method: 'GET', headers });
                if (!response.ok) return;
                const result = await response.json();
                if (!result.isUserPurchaseAvailable || !result.payload) return;
                updateParticipant(0, {
                    name: result.payload.firstName || '',
                    surname: result.payload.lastName || '',
                    birthdate: result.payload.birthdate || ''
                });
            } catch (error) {
                console.error('Precompilazione dati partecipante non riuscita:', error);
            }
        };
        prefill();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const updateParticipant = (index, patch) => {
        updateBooking({
            participants: booking.participants.map((participant, i) => i === index ? { ...participant, ...patch } : participant)
        });
    };

    const canAddParticipant = !isPerPerson &&
        (bundleType !== 'flat' || bundle.maxQty == null || booking.participants.length < Number(bundle.maxQty));
    const addParticipant = () => {
        updateBooking({ participants: [...booking.participants, { name: '', surname: '', birthdate: '' }] });
    };
    const removeParticipant = (index) => {
        updateBooking({ participants: booking.participants.filter((participant, i) => i !== index) });
    };

    const setCustomAnswer = (label, answer) => {
        updateBooking({ customFieldAnswers: { ...booking.customFieldAnswers, [label]: answer } });
    };

    const canProceed = isParticipantsStepComplete(activityData, booking);

    // Guardie: senza gli step precedenti completi si torna indietro
    if (!optionComplete) return <Navigate to="../opzione" replace />;
    if (!scheduleComplete) return <Navigate to="../data" replace />;

    return (
        <div className="w-full flex flex-col gap-6 items-center">
            <div className="text-center flex flex-col gap-2">
                <h2>{t('BookingWizard.participants.title')}</h2>
                <p className="text-neutral-500">{t('BookingWizard.participants.intro')}</p>
            </div>

            <div className="w-full flex flex-col gap-4">
                {
                    booking.participants.map((participant, index) => (
                        <div key={index} className="flex flex-col gap-2 p-4 border-2 rounded-xl bg-white">
                            <div className="flex items-center justify-between">
                                <label className="font-bold">{t('BookingWizard.participants.participantN', { n: index + 1 })}</label>
                                {
                                    !isPerPerson && index > 0 &&
                                    <button className="text-sm text-red-500" onClick={() => removeParticipant(index)}>{t('BookingWizard.participants.remove')}</button>
                                }
                            </div>
                            {
                                index === 0 &&
                                <>
                                    <label className="text-xs text-neutral-500" htmlFor={`name-${index}`}>{t('SendRequest.name')}</label>
                                    <input type="text" id={`name-${index}`} value={participant.name}
                                        onChange={(event) => updateParticipant(index, { name: event.target.value })} />
                                    <label className="text-xs text-neutral-500" htmlFor={`surname-${index}`}>{t('SendRequest.surname')}</label>
                                    <input type="text" id={`surname-${index}`} value={participant.surname}
                                        onChange={(event) => updateParticipant(index, { surname: event.target.value })} />
                                </>
                            }
                            <label className="text-xs text-neutral-500" htmlFor={`birthdate-${index}`}>{t('SendRequest.birthdate')}</label>
                            <input type="date" id={`birthdate-${index}`} value={participant.birthdate}
                                onChange={(event) => updateParticipant(index, { birthdate: event.target.value })} />
                        </div>
                    ))
                }
                {
                    canAddParticipant &&
                    <button className="text-[#225424] font-bold text-start" onClick={addParticipant}>{t('BookingWizard.participants.addParticipant')}</button>
                }
            </div>

            {/* Domande personalizzate dell'attività (prima delle Osservazioni) */}
            {
                activityData.bookingCustomFields && activityData.bookingCustomFields.length > 0 &&
                <div className="w-full flex flex-col gap-3">
                    {
                        activityData.bookingCustomFields.map((field) => (
                            <div key={field._id || field.label} className="flex flex-col gap-1">
                                <label className="text-sm" htmlFor={`custom-${field.label}`}>{field.label}</label>
                                <textarea
                                    id={`custom-${field.label}`}
                                    className="resize-none h-16"
                                    maxLength={FREE_TEXT_MAX_LENGTH}
                                    value={booking.customFieldAnswers[field.label] || ''}
                                    onChange={(event) => setCustomAnswer(field.label, event.target.value)}
                                ></textarea>
                            </div>
                        ))
                    }
                </div>
            }

            {/* Osservazioni */}
            <div className="w-full flex flex-col gap-1">
                <label className="text-sm" htmlFor="observations">{t('BookingWizard.participants.observations')}</label>
                <textarea
                    id="observations"
                    className="resize-none h-24"
                    maxLength={FREE_TEXT_MAX_LENGTH}
                    value={booking.observations}
                    onChange={(event) => updateBooking({ observations: event.target.value })}
                ></textarea>
                <div className="text-xs text-neutral-400 text-end">{t('BookingWizard.charsLeft', { count: FREE_TEXT_MAX_LENGTH - booking.observations.length })}</div>
            </div>

            {
                canProceed &&
                <button className="bg-[#225424] py-3 px-6 text-white rounded-xl w-full md:w-fit" onClick={() => navigate('../riepilogo')}>
                    {t('BookingWizard.participants.goToSummary')}
                </button>
            }
        </div>
    );
};

export default StepParticipants;
